import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {ApolloQueryResult} from 'apollo-client';
import * as React from 'react';
import {useCallback, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Table} from 'semantic-ui-react';

import {
  EntityIntegrationsQuery,
  EntityQuery,
  GenericWebhookIntegration,
  Integration,
} from '../../generated/graphql';
import {TeamsPlanCheckoutModal} from '../../pages/Billing/Checkout/TeamsPlanCheckoutModal';
import {Entity} from '../../types/graphql';
import {Org, StripeElementsComp} from '../../util/accounts/pricing';
import {maybePluralize} from '../../util/uihelpers';
import * as S from '../Alerts.styles';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {useWebhooksFromEntity} from './hooks';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ViewWebhookDrawer} from './ViewWebhookDrawer';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {WebhookActions} from './WebhookActions';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {WebhookDrawer} from './WebhookDrawer';
import {WebhookTableColumnURL} from './WebhookTableColumnURL';

export function isGenericWebhookIntegration(
  integration: Integration
): integration is GenericWebhookIntegration {
  return (integration as any).__typename === 'GenericWebhookIntegration';
}

export type GenericWebhook = {
  name: string;
  id: string;
  urlEndpoint: string;
  accessTokenRef?: string;
  secretRef?: string;
  createdAt: string;
};

export function toGenericWebhookIntegration(
  integration: Integration
): GenericWebhook | undefined {
  if (isGenericWebhookIntegration(integration)) {
    return {
      ...integration,
      accessTokenRef: integration.accessTokenRef ?? undefined,
      secretRef: integration.secretRef ?? undefined,
    };
  }
  return undefined;
}

type WebhooksTableProps = {
  entity: Entity | EntityQuery['entity'] | EntityIntegrationsQuery['entity'];
  isPersonalEntity?: boolean;
  isPremiumFeatureEnabled?: boolean;
  orgToUpgrade?: Org;
  entityRefetch?: () =>
    | Promise<ApolloQueryResult<EntityQuery>>
    | Promise<ApolloQueryResult<EntityIntegrationsQuery>>;
};

export const WebhooksTable = ({
  entity,
  isPersonalEntity,
  isPremiumFeatureEnabled,
  orgToUpgrade,
  entityRefetch,
}: WebhooksTableProps) => {
  // TODO: paginate this view?
  const genericWebhooks: GenericWebhook[] = useWebhooksFromEntity(entity);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [viewWebhook, setViewWebhook] = useState<GenericWebhook>();
  const closeViewDrawer = useCallback(() => {
    setIsViewDrawerOpen(false);
    setViewWebhook(undefined);
  }, [setViewWebhook, setIsViewDrawerOpen]);
  const [isWebhookDrawerOpen, setIsWebhookDrawerOpen] = useState(false);

  const [openCheckoutModal, setOpenCheckoutModal] = useState(false);

  const WebhookTableRows: React.FC<{
    genericWebhooks: GenericWebhook[];
    setIsViewDrawerOpen: (open: boolean) => void;
    setViewWebhook: (webhook: GenericWebhook) => void;
    entity: Entity | EntityQuery['entity'] | EntityIntegrationsQuery['entity'];
    entityRefetch?: () =>
      | Promise<ApolloQueryResult<EntityQuery>>
      | Promise<ApolloQueryResult<EntityIntegrationsQuery>>;
    isPersonalEntity?: boolean;
    isPremiumFeatureEnabled?: boolean;
  }> = ({
    genericWebhooks,
    setIsViewDrawerOpen,
    setViewWebhook,
    entity,
    entityRefetch,
    isPersonalEntity,
    isPremiumFeatureEnabled,
  }) => {
    return (
      <>
        {genericWebhooks.map((webhook: GenericWebhook) => {
          const hasSecrets =
            webhook.secretRef != null || webhook.accessTokenRef != null;
          return (
            <Table.Row key={webhook.id}>
              <Table.Cell>
                <div className="flex items-center gap-8">
                  <Icon name="webhook" />
                  <div
                    className="max-w-[15rem] cursor-pointer truncate font-semibold text-teal-650 hover:text-teal-500"
                    title={webhook.name}
                    onClick={() => {
                      setIsViewDrawerOpen(true);
                      setViewWebhook(webhook);
                    }}>
                    {webhook.name}
                  </div>
                  {hasSecrets && (
                    <Tooltip
                      content="Created with a secret"
                      trigger={
                        <Icon
                          className="text-gold-600"
                          name="key-admin-private"
                        />
                      }
                    />
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div className="w-[22rem]">
                  <WebhookTableColumnURL url={webhook.urlEndpoint} />
                </div>
              </Table.Cell>
              <Table.Cell>
                <WebhookActions
                  entityName={entity?.name ?? ''}
                  webhook={webhook}
                  entityRefetch={entityRefetch}
                  isPersonalEntity={isPersonalEntity}
                  isPremiumFeatureEnabled={isPremiumFeatureEnabled}
                  existingWebhooks={genericWebhooks}
                />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Tailwind>
        <div className="mb-8 mt-8 flex items-center justify-between">
          {genericWebhooks.length > 0 &&
            `${maybePluralize(genericWebhooks.length, 'webhook')}`}
          {isPremiumFeatureEnabled && (
            <Button
              variant="primary"
              size="small"
              className="p-8"
              onClick={() => setIsWebhookDrawerOpen(true)}>
              New webhook
            </Button>
          )}
        </div>
        <div className="webhooks max-h-[18rem] overflow-auto">
          {genericWebhooks.length > 0 && (
            <Table className="webhooks--table" basic="very">
              <Table.Body>
                <WebhookTableRows
                  genericWebhooks={genericWebhooks}
                  setIsViewDrawerOpen={setIsViewDrawerOpen}
                  setViewWebhook={setViewWebhook}
                  entity={entity}
                  entityRefetch={entityRefetch}
                  isPersonalEntity={isPersonalEntity}
                  isPremiumFeatureEnabled={isPremiumFeatureEnabled}
                />
              </Table.Body>
            </Table>
          )}
          {!isPremiumFeatureEnabled && orgToUpgrade != null && (
            <S.AlignedSection>
              <S.UpgradeModalLink onClick={() => setOpenCheckoutModal(true)}>
                Upgrade your plan
              </S.UpgradeModalLink>
              <span>&nbsp;in order to access this feature</span>
            </S.AlignedSection>
          )}
          {!isPremiumFeatureEnabled && orgToUpgrade == null && (
            <span className="flex items-center justify-center">
              Upgrade your plan in order to access this feature
            </span>
          )}
        </div>
        <WebhookDrawer
          open={isWebhookDrawerOpen}
          closeDrawer={() => {
            setIsWebhookDrawerOpen(false);
          }}
          drawerMode="Create"
          entityName={entity?.name ?? ''}
          refetch={entityRefetch}
          existingWebhooks={genericWebhooks}
          isPersonalEntity={isPersonalEntity}
          webhook={undefined}
        />
        <ViewWebhookDrawer
          open={isViewDrawerOpen}
          closeDrawer={closeViewDrawer}
          genericWebhook={viewWebhook}
          entityName={entity?.name ?? ''}
          isPersonalEntity={isPersonalEntity}
        />
      </Tailwind>

      {openCheckoutModal && orgToUpgrade !== undefined && (
        <StripeElementsComp>
          <TeamsPlanCheckoutModal
            org={orgToUpgrade}
            onTransactionCompleted={() => {
              toast('Success! Subscription updated to pro plan', {
                position: 'top-right',
                autoClose: 25000,
              });
              setOpenCheckoutModal(false);
              entityRefetch?.();
            }}
            onClose={() => setOpenCheckoutModal(false)}
          />
        </StripeElementsComp>
      )}
    </>
  );
};
